import React, { useState, useEffect } from "react";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import AppNavigation from "../../components/navigation/appNavigation/AppNavigation";
import StepProgress from "../../components/stepper/StepProgress";
import GenerateQuestionsModal from "../../components/modals/GenerateQuestionsModal";
import { commonErrorToastMessage } from "../../components/toastify/AllToastifyErrors";
import { json, useNavigate } from "react-router-dom";
import { getDecryptedBatch } from "../../utils/cryptoUtils";
import { getEduvateQuestionAnswer } from "../../api/getEduvateQuestionAnswer";
import { AREAS , SUBCATEGORIES , QUESTION_TYPES , QUESTION_TYPES_RIGHT, getQuestionLimit } from "../../utils/QuestionLimit";
import { fetchSampleQuestionSet } from "../../api/fetchSampleQuestionSet";

const SUBCATEGORY_SELECT_LIMIT = 1;

const QuestionsSelection = () => {
    const navigate = useNavigate();

   // ── Non-sensitive session keys — plain localStorage is fine for these ──────
  const isSampleButtonClicked = localStorage.getItem("isSampleButtonClicked") === "true";
  const sampleCategory = localStorage.getItem("category");
  const sampleSubCategory = localStorage.getItem("subCategory");
  const sampleDocId = localStorage.getItem("doc_id");
  const title                 = localStorage.getItem("title");
  const word_count_total      = parseInt(localStorage.getItem("wordCount") || "1000", 10);

  // ── Encrypted localStorage values — live in state ─────────────────────────
  const [userEmail, setUserEmail]                   = useState("");
  const [userGPTKey, setUserGPTKey]                 = useState("LIMITED");
  const [TIERTYPE, setTIERTYPE]                     = useState("");
  const [remainingQuota, setRemainingQuota]         = useState(0);
  const [QuestionLimitNo, setQuestionLimitNo]       = useState(0);

  // ── Step 1: Decrypt all needed values once on mount ──────────────────────
  useEffect(() => {
    const loadSecureData = async () => {
      const {
        email : decryptedEmail,
        userGPTKey:     decryptedGPTKey,
        TIERTYPE:       decryptedTierType,
        UserAttempts:   decryptedAttempts,
        QuestionLimitNo: decryptedQLimit,
      } = await getDecryptedBatch([
        "email",
        "userGPTKey",
        "TIERTYPE",
        "UserAttempts",
        "QuestionLimitNo",
      ]);

      setUserEmail(decryptedEmail ?? "");
      setUserGPTKey(decryptedGPTKey ?? "LIMITED");
      setTIERTYPE(decryptedTierType ?? "");
      setRemainingQuota(parseInt(decryptedAttempts) || 0);
      setQuestionLimitNo(parseInt(decryptedQLimit) || 0);
    };

    loadSecureData();
  }, []);

  const user_tier = TIERTYPE === 'tier2_monthly' || TIERTYPE === "tier2_annually"? 2 : 1; 

  const user_remaining_quota  = remainingQuota;
  const max_questions_per_chunk = QuestionLimitNo;

  // Add a new state variable to track the loading state
  const defaultArea = isSampleButtonClicked ? sampleCategory : AREAS[0];
  const [selectedAreas, setSelectedAreas] = useState([defaultArea]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isGenerateQuestionsModalOpen, setIsGenerateQuestionsModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const activeStep = 2;


  useEffect(() => {
    if(isSampleButtonClicked) {
      let subcategoryKey = `${sampleCategory}|${sampleSubCategory}`; // Create unique key
      let arr = [subcategoryKey];
      localStorage.setItem("selectedSubcategories", JSON.stringify(arr));
      setSelectedSubcategories(arr);
    }
  }, []);

  const [questionCounts, setQuestionCounts] = useState({
    mcq: 1, fill: 1, tf: 1, summary: true,
    interpritive: 1,descriptive: 1,short: 1,scenario: 1,
  });
  const [checked, setChecked] = useState({
    mcq: false,fill: false,tf: false,summary: true,
    interpritive: false,descriptive: false,short: false,scenario: false,
  });


  // Function to handle closing the modal
  const handleModalClose = () => setIsModalOpen(false); 

  const handleAreaClick = (area) => {
    setSelectedAreas([area]);
    setSelectedSubcategories([]);
    localStorage.setItem("selectedSubcategories", JSON.stringify([]));
  };

  const handleSubcategoryClick = (subcategory, area) => {
  const subcategoryKey = `${area}|${subcategory}`; // Create unique key
  localStorage.setItem("selectedSubcategories", JSON.stringify(subcategoryKey));
  setSelectedSubcategories((prev) => {
    let updated;
    if (prev.includes(subcategoryKey)) {
      updated = prev.filter((s) => s !== subcategoryKey);
    } else if (prev.length < SUBCATEGORY_SELECT_LIMIT) {
      updated = [...prev, subcategoryKey];
    } else {
      // Limit reached, do not add more
      updated = prev;
    }
    // Store in localStorage
    localStorage.setItem("selectedSubcategories", JSON.stringify(updated));
    return updated;
  });
  // FIXED: Update the selected area when subcategory is clicked
    if (!selectedAreas.includes(area)) {
      setSelectedAreas([area]);
    }
};

  const handleCheck = (key) => {
    setQuestionCounts((prev) => {
      // If already checked, allow unchecking
      if (prev[key] > 0) {
        return { ...prev, [key]: 0 };
      }
      // If not checked, only allow checking if totalSelected < questionLimit
      if (totalSelected < questionLimit) {
        return { ...prev, [key]: 1 };
      }
      // Otherwise, do nothing
      return prev;
    });
  };

  const handleCountChange = (key, value) => {
    const num = parseInt(value, 10);
    setQuestionCounts((prev) => ({ ...prev, [key]: num }));
    setChecked((prev) => ({ ...prev, [key]: num > 0 }));
  };

  

  const questionLimit = getQuestionLimit(word_count_total);

  const totalSelected =
    (parseInt(questionCounts.mcq) || 0) +
    (parseInt(questionCounts.fill) || 0) +
    (parseInt(questionCounts.tf) || 0) +
    (parseInt(questionCounts.descriptive) || 0) +
    (parseInt(questionCounts.interpritive) || 0) +
    (parseInt(questionCounts.short) || 0) +
    (parseInt(questionCounts.scenario) || 0);


   // If search is present and has at least 2 characters, show all matching areas and subcategories
  const shouldSearch = search.trim().length >= 2;

  const filteredAreasWithSubcategories = shouldSearch
  ? AREAS.map((area) => {
      const subs = SUBCATEGORIES[area] || [];
      const matchingSubs = subs.filter((sub) =>
        sub.toLowerCase().includes(search.toLowerCase())
      );
      return {
        area,
        subcategories: matchingSubs,
        hasMatches: matchingSubs.length > 0,
        allSubcategories: subs
      };
    }).filter(item => item.hasMatches) // Only show areas with matches
  : [
      {
        area: selectedAreas[0],
        subcategories: SUBCATEGORIES[selectedAreas[0]] || [],
        hasMatches: false,
        allSubcategories: SUBCATEGORIES[selectedAreas[0]] || []
      },
    ];


   function highlightTextInline(text, search , shouldHighlight = true) {
  if (!search) return text;
  const regex = new RegExp(`(${search})`, "ig");
  const parts = text.split(regex);
  
  return (
    <span className="inline-flex flex-wrap items-center justify-center leading-tight">
      {parts.map((part, i) =>
        regex.test(part) && shouldHighlight ? (
          <span 
            key={i} 
            // className="bg-yellow-300 text-black px-1 rounded-sm mx-0.5 font-bold inline-block"
            // className="bg-yellow-300 text-black px-1 rounded-sm mx-0.5 font-bold inline-block"
            // style={{ lineHeight: '1.2' }}
          >
            {part}
          </span>
        ) : (
          <span key={i} className="inline-block">{part}</span>
        )
      )}
    </span>
  );
}

async function getSampleQuestionSet(category, subCategory, docId) {
  const userType = "gpt_user";
  const { data, status } = await fetchSampleQuestionSet(userEmail, userGPTKey, userType, category, subCategory, docId);
  if ([200, 201].includes(status)) {
    return data;
  }
  return null;
}

  const handleQuestionAnswer = async () => {
    try {
      // This is mechanism is only for Sample Text
      setIsLoading(false);
      setIsGenerateQuestionsModalOpen(true);

      if (isSampleButtonClicked) {
        const remainingQuestionSets = localStorage.getItem("UserAttempts");
        const sample_set = await getSampleQuestionSet(sampleCategory, sampleSubCategory, sampleDocId);

        // Generate random delay between 50-70 seconds
        const randomDelay = 5000;
        
        // Wait for the random delay using a Promise
        await new Promise(resolve => setTimeout(resolve, randomDelay));
        
        navigate("/question", {
          state: {
            generatedQuestions: sample_set.questions.questionset,
            userAttempts: remainingQuestionSets,
          },
        });
      } else {
        // !TODO : THIS PART IS REMAINING, DO IT LATER....
        try {
          const user_type = "gpt_user";

          if ( !selectedAreas ||selectedAreas === null || selectedAreas.length === 0) {
            commonErrorToastMessage("Please select the Area and Subcategory.");
            return;
          }

          if (!selectedSubcategories || selectedSubcategories === null || selectedSubcategories.length === 0) {
            commonErrorToastMessage(
              "Select one subcategory to proceed."
            );
            return;
          }

          if (user_remaining_quota === 0 && userGPTKey === "LIMITED") {
            commonErrorToastMessage(
              "You have exhausted your monthly 5 attempts. You can upgrade to Plan."
            );
            return;
          }

          // Validation: At least one question type must be selected
          const isAnyQuestionTypeSelected = QUESTION_TYPES.concat(
            QUESTION_TYPES_RIGHT
          ).some((qt) => parseInt(questionCounts[qt.key]) > 0);

          if (!isAnyQuestionTypeSelected) {
            commonErrorToastMessage(
              "Please select at least one question type."
            );
            return;
          }

          const QAreqbody = JSON.parse(localStorage.getItem("QAreqbody"));
          const { doc_id, total_words, title, text, created_at, Status } =
            QAreqbody;
          // Prepare the parameters for the API
          const params = {
            no_of_mcq: parseInt(questionCounts.mcq) || 0,
            no_of_fib: parseInt(questionCounts.fill) || 0,
            no_of_truefalse: parseInt(questionCounts.tf) || 0,
            no_of_interpritive: parseInt(questionCounts.interpritive) || 0,
            no_of_descriptive: parseInt(questionCounts.descriptive) || 0,
            no_of_shqa: parseInt(questionCounts.short) || 0,
            no_of_sbq: parseInt(questionCounts.scenario) || 0,
            sector_name: selectedAreas[0] || "",
            segment_name: selectedSubcategories, // This is an array
          };

          const response = await getEduvateQuestionAnswer(
            userEmail,
            user_type,
            userGPTKey,
            doc_id,
            total_words,
            user_tier,
            max_questions_per_chunk,
            user_remaining_quota,
            title,
            text,
            created_at,
            Status,
            parseInt(questionCounts.mcq) || 0,
            parseInt(questionCounts.fill) || 0,
            parseInt(questionCounts.tf) || 0,
            parseInt(questionCounts.interpritive) || 0,
            parseInt(questionCounts.descriptive) || 0,
            parseInt(questionCounts.short) || 0,
            parseInt(questionCounts.scenario) || 0,
            selectedAreas[0] || "",
            // selectedSubcategories && Array.isArray(selectedSubcategories)
            //   ? selectedSubcategories.join(",")
            //   : "" // Send empty string if null/undefined
            selectedSubcategories && Array.isArray(selectedSubcategories)
            ? selectedSubcategories.map(item => item.split('|')[1]).join(',') // Extract subcategory part
            : ""
          );

          // Log the API response
          navigate("/question", {
            state: {
              generatedQuestions: response.data,
            },
          });

          if (
            response.data.response.no_ques !== 0 &&
            !response.data.response.error
          ) {
            // ! Even if questions aren't generated, the 'RemainingQuestionSets' count decreases by 1, but this updated count isn't reflected on the user interface.

            navigate("/question", {
              state: {
                generatedQuestions: response.data,
                userAttempts: response.data.updatedAttempts,
              },
            });
          } else {
            console.log(
              "Error without questions : ",
              response.data.response.error
            );
            console.error("Error while generating questions.");
            commonErrorToastMessage("Error while generating questions.");
            // onClose();
            setIsGenerateQuestionsModalOpen(false);
            setIsModalOpen(false);
            return;
          }
        } catch (error) {
          console.error("Error while making the API request:", error);
          commonErrorToastMessage("Error while generating questions.");
          setIsLoading(false);
          setIsModalOpen(false);
          setIsGenerateQuestionsModalOpen(false);
          // onClose();
        }
      }
      setIsGenerateQuestionsModalOpen(false);
      setIsLoading(false);
    } catch (err) {
      console.error("Error while making the API request :", err.message);
      commonErrorToastMessage("An error occurred while curating the content.");
      setIsLoading(false);
      setIsGenerateQuestionsModalOpen(false);
    } finally {
      setIsLoading(false);
      setIsGenerateQuestionsModalOpen(false);
    }
  };
  
  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0">
        <AppNavigation />
        <div className="w-auto h-auto step-progress">
          {/* Pass the activeStep value to the StepProgress component */}
          <StepProgress activeStep={activeStep} />
        </div>
      </div>

      {/* Scrollable Content Section */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 md:px-12 lg:px-28 pb-8">
          <div className="flex items-center mb-2 mt-[12px]">
            <span className="text-base font-semibold text-gray-500 mr-2">
              Search Subcategories 
            </span>
            <span className="text-red-500 text-lg">*</span>
            <input
              type="text"
              placeholder="Search"
              className={`ml-5 px-3 py-1 border-b-2 border-gray w-56 text-xs ${isSampleButtonClicked ? "opacity-50 cursor-not-allowed" : ""}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={isSampleButtonClicked}
            />
          </div>
          <div className="text-gray-500 text-sm mb-[12px] mt-[12px]">
          Select Category{" "} 
          <span className="font-medium text-sm"><sapn className='text-orange-500'>(Select only one)</sapn></span>
          </div>
            <div className="flex flex-wrap gap-4 mb-[20px] mt-[10px]">
  {AREAS.map((area) => {
    const areaHasMatches = search 
      ? (SUBCATEGORIES[area] || []).some(sub => 
          sub.toLowerCase().includes(search.toLowerCase())
        )
      : false;
    
    return (
      <button
        key={area}
        className={`px-2 py-2 rounded border text-[12px] font-medium transition-all duration-200 w-40 h-16 flex flex-col items-center justify-center text-center leading-tight relative ${
          selectedAreas.includes(area)
            ? "bg-[#1462af] border-[#1462af] text-[#fff]"
            : search && areaHasMatches
            ? "bg-[#fff3cd] border-[#ffc107] text-[#856404]"
            : search && !areaHasMatches
            ? "bg-[#f8f9fa] border-[#dee2e6] text-[#6c757d] opacity-60" // Dim areas without matches
            : "bg-[#ffffff] border-[#8eacf1] text-[#5380ea]"
        } ${
          (isSampleButtonClicked && !selectedAreas.includes(area)) ? "opacity-50 cursor-not-allowed" : ""
        }`}
        onClick={isSampleButtonClicked ? undefined : () => handleAreaClick(area)}
        disabled={isSampleButtonClicked && !selectedAreas.includes(area)}
      >
        <div className="flex flex-col items-center justify-center h-full w-full text-center">
          {highlightTextInline(area, search , false)}
        </div>
        {selectedAreas.includes(area) && (
          <div className="absolute top-1/2 -right-3 transform -translate-y-1/2 w-6 h-6 bg-white rounded-full flex items-center justify-center border-2 border-[#1462af]">
            <svg className="w-3 h-3 text-[#1462af]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        {search && areaHasMatches && !selectedAreas.includes(area) && (
          <div className="absolute top-1 right-1 w-2 h-2 bg-[#ffc107] rounded-full"></div>
        )}
      </button>
    );
  })}
</div>
    <hr className="flex-grow border-gray-300 mx-4" />
    <div className="text-gray-500 text-sm mb-[10px] mt-[5px]">
          Select Subcategory {" "}
          <span className="font-medium text-sm"><span className="text-orange-500">(Select only one)</span></span>
    </div>
          {/* Subcategories Section */}
        {filteredAreasWithSubcategories.length > 0 ? (
  filteredAreasWithSubcategories.map(({ area, subcategories, hasMatches }) => (
    <>
    <div key={area} className="mb-[10px]">
      <div className={`font-normal mb-[5px] ${
        search && hasMatches 
          ? 'text-[#856404] font-semibold'
          : 'text-[#1462af]'
      }`}>
        {highlightTextInline(area, search)}
        {search && hasMatches && (
          <span className="ml-2 text-xs bg-[#ffc107] text-[#856404] px-2 py-1 rounded">
            {subcategories.length} match{subcategories.length !== 1 ? 'es' : ''}
          </span>
        )}
      </div>
      <div className="bg-[#1462af] text-white p-[10px] rounded">
        <div className="flex flex-wrap gap-3">
          {subcategories.length > 0 ? (
  subcategories.map((subcategory) => {
    const subcategoryKey = `${area}|${subcategory}`;
    return (
      <button
        key={subcategoryKey}
        disabled={
          !selectedSubcategories.includes(subcategoryKey) &&
          selectedSubcategories.length >= SUBCATEGORY_SELECT_LIMIT
        }
        className={`px-3 py-1 rounded text-[12px] border transition-all duration-200 relative ${
          selectedSubcategories.includes(subcategoryKey)
            ? "bg-white text-[#2563eb] border-white"
            : "bg-transparent text-white border-white/30 hover:border-white"
        } ${
          !selectedSubcategories.includes(subcategoryKey) &&
          selectedSubcategories.length >= SUBCATEGORY_SELECT_LIMIT
            ? "opacity-50 cursor-not-allowed"
            : ""
        }`}
        onClick={isSampleButtonClicked ? undefined : () => handleSubcategoryClick(subcategory, area)}
      >
        {highlightTextInline(subcategory, search)}
        {selectedSubcategories.includes(subcategoryKey) && (
          <div className="absolute top-1/2 -right-3 transform -translate-y-1/2 w-6 h-6 bg-white rounded-full flex items-center justify-center border-2 border-[#1462af]">
            <svg className="w-3 h-3 text-[#1462af]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </button>
    );
  })
) : (
  <span className="text-xs text-white/60">
    No matching subcategories found in this area
  </span>
)}
        </div>
      </div>
    </div>
    </>
  ))
) : search ? (
  // Show "No results found" message only when searching and no subcategories match
  <div className="w-full text-center py-8">
    <div className="bg-[#f8f9fa] border border-[#dee2e6] rounded-lg p-6 text-gray-500">
      <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
      </svg>
      <h3 className="text-lg font-semibold mb-2">No results found</h3>
      <p className="text-sm">No subcategories match "<strong>{search}</strong>"</p>
      <p className="text-xs mt-2">Try searching with different keywords</p>
    </div>
  </div>
) : null}


          <hr className="my-[10px] mr-20 ml-20" />

          {/* Select Question Type */}
          <div className="mt-[10px]">
            <div className="text-xs font-semibold text-[#212529] mb-2 flex items-center">
              Select Question Type{" "}
              <span className="text-red-500 ml-1 mr-2">*</span>
              <div className="flex gap-2">
                {
                  isSampleButtonClicked ? (
                    <div className="text-xs flex justify-center bg-[#1462b0] text-white border-1 rounded-md sm:h-[30px]">
                  <p className="p-1 mt-[3px]">{`Selected: 7/7`}</p>
                </div>
                  ) :(
                    <div className="text-xs flex justify-center bg-[#1462b0] text-white border-1 rounded-md sm:h-[30px]">
                  <p className="p-1 mt-[3px]">{`Selected: ${totalSelected}/${questionLimit}`}</p>
                </div>
                  )
                }
                <div className="flex justify-center bg-[#f3f3ff] pt-[5px] px-4 py-1 rounded shadow text-[#00000] text-xs font-medium border border-[#e3eaf3] sm:w-[83vh]">
                  {`Considering the source content is ${word_count_total} words, a total of ${questionLimit} questions can be generated.`}
                </div>
              </div>
            </div>
            <div className="flex justify-between items-center mb-4">
              <div className="w-fit text-gray-500 text-xs">
                Choose the type of question and specify the number of questions
                for each type.
              </div>
            </div>

            <div className="flex flex-row gap-8">
              {/* Left column */}
              <div className="flex-1">
                {QUESTION_TYPES &&
                  QUESTION_TYPES.map((qt) => (
                    <div key={qt.key} className="flex items-center mb-4">
                      <input
                        type="checkbox"
                        checked={parseInt(questionCounts[qt.key]) > 0}
                        onChange={() => handleCheck(qt.key)}
                        // className="w-5 h-5 accent-[#1976d2] mr-3"
                        id={qt.key}
                        disabled={
                          // qt?.key === "summary"
                          // ? false // Always enabled for "Generate Summary"
                          // :
                          isSampleButtonClicked ? true : parseInt(questionCounts[qt.key]) === 0 &&
                          totalSelected >= questionLimit
                        }
                        className={`w-5 h-5 accent-[#1976d2] mr-3 ${
                          parseInt(questionCounts[qt.key]) === 0 &&
                          totalSelected >= questionLimit
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }${
                          isSampleButtonClicked && "opacity-50 cursor-not-allowed"
                        }`}
                      />
                      <label
                        htmlFor={qt.key}
                        className="text-gray-500 text-xs flex-1"
                      >
                        {qt.label}
                      </label>
                      {qt.key !== "summary" && (
                        <>
                          <Select
                            size="small"
                            value={questionCounts[qt.key]}
                            onChange={(e) =>
                              handleCountChange(qt.key, e.target.value)
                            }
                            // disabled={
                            //   !checked[qt.key] &&
                            //   totalSelected >= questionLimit
                            // }
                            disabled={isSampleButtonClicked ? true : parseInt(questionCounts[qt.key]) === 0}
                            className="w-[4.5rem] h-[1.7rem] mx-2"
                            sx={{
                              backgroundColor: checked[qt.key]
                                ? "#fff"
                                : "#f3f3f3",
                              fontSize: 15,
                              height: 30,
                            }}
                          >
                            {[
                              ...Array(
                                Math.max(
                                  1,
                                  questionLimit -
                                    totalSelected +
                                    (parseInt(questionCounts[qt.key]) || 0) +
                                    1
                                )
                              ).keys(),
                            ]
                              .slice(1)
                              .map((num) => (
                                <MenuItem key={num} value={num}>
                                  {num}
                                </MenuItem>
                              ))}
                          </Select>
                          <span className="text-gray-500 text-xs">
                            Questions
                          </span>
                        </>
                      )}
                    </div>
                  ))}
              </div>
              <hr className="mx-2" />
              {/* Right column */}
              <div className="flex-1 rounded-lg relative">
                <div className="">
                  {QUESTION_TYPES_RIGHT &&
                    QUESTION_TYPES_RIGHT.map((qt) => (
                      <div key={qt.key} className="flex items-center mb-4">
                        <input
                          type="checkbox"
                          checked={parseInt(questionCounts[qt.key]) > 0}
                          onChange={() => handleCheck(qt.key)}
                          // className="w-5 h-5 accent-[#1976d2] mr-3"
                          id={qt.key}
                          disabled={
                            isSampleButtonClicked ? true : parseInt(questionCounts[qt.key]) === 0 &&
                            totalSelected >= questionLimit
                          }
                          className={`w-5 h-5 accent-[#1976d2] mr-3 ${
                            parseInt(questionCounts[qt.key]) === 0 &&
                            totalSelected >= questionLimit
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }${
                            isSampleButtonClicked && "opacity-50 cursor-not-allowed"
                          }`}
                        />
                        <label
                          htmlFor={qt.key}
                          className="text-gray-500 text-xs flex-1"
                        >
                          {qt.label}
                        </label>
                        <Select
                          size="small"
                          value={questionCounts[qt.key]}
                          onChange={(e) =>
                            handleCountChange(qt.key, e.target.value)
                          }
                          // disabled={
                          //   !checked[qt.key] &&
                          //   totalSelected >= questionLimit
                          // }
                          disabled={isSampleButtonClicked ? true : parseInt(questionCounts[qt.key]) === 0}
                          className="w-20 mx-2"
                          sx={{
                            backgroundColor: checked[qt.key]
                              ? "#fff"
                              : "#f3f3f3",
                            fontSize: 15,
                            height: 30,
                          }}
                        >
                          {[
                            ...Array(
                              Math.max(
                                1,
                                questionLimit -
                                  totalSelected +
                                  (parseInt(questionCounts[qt.key]) || 0) +
                                  1
                              )
                            ).keys(),
                          ]
                            .slice(1)
                            .map((num) => (
                              <MenuItem key={num} value={num}>
                                {num}
                              </MenuItem>
                            ))}
                        </Select>
                        <span className="text-gray-500 text-xs">Questions</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          <hr className="my-4 mr-20 ml-20" />

          <div className="flex justify-end mt-[-15px]">
            <Tooltip
              title="Proceed your Question Selection"
              arrow
              placement="top"
              TransitionComponent={Zoom}
              TransitionProps={{ timeout: 300 }}
            >
              <button
                onClick={handleQuestionAnswer}
                className="rounded py-2 mt-2 h-[7vh]  hover:opacity-90 bg-[#c6530c] text-white text-sm  px-4  "
              >
                Proceed
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {isGenerateQuestionsModalOpen && (
        <GenerateQuestionsModal
          isOpen={isGenerateQuestionsModalOpen} // Pass the state to control whether the modal is open or not
          onClose={handleModalClose} // Pass the function to close the modal and redirect
          // Pass the time taken to generate the response as a prop
        />
      )}
    </div>
  );
};

export default QuestionsSelection;